import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { Resizable } from 're-resizable';
import PropTypes from 'prop-types';
import { TableCell, TableSortLabel } from '@mui/material';
import customStyles from './ListComponent.module.scss';

export default function SortableItem(props) {
    const {
        id,
        rearrangeColumns,
        onRequestSort,
        items,
        index,
        item,
        enableColumnRearrange,
        orderBy,
        order,
        resizableHeader,
        isGroup,
        isExpanded,
        getIcon,
        createSortHandler,
        classes,
        activeObject,
        setResizable,
        isResizing,
        isSortableOverlay,
    } = props;

    const {
        attributes,
        isDragging,
        listeners,
        setNodeRef,
        transition,
        transform,
    } = useSortable({
        id,
        disabled: !enableColumnRearrange || typeof rearrangeColumns !== 'function' || isResizing,
    });

    const transformToString = (transformation) => {
        if (!transformation) return undefined;
        const { x, y, scaleX, scaleY } = transformation;
        return `translate(${x}px, ${y}px) scale(${scaleX}, ${scaleY})`;
    };

    const style = {
        visibility: (isDragging && activeObject) ? 'hidden' : 'visible',
        transform: activeObject && transform ? transformToString(transform) : undefined,
        transition,
        cursor: enableColumnRearrange ? 'grab' : 'default',
    };

    const resizeEnd = (width) => {
        if (typeof rearrangeColumns === 'function') {
            const newItem = [...items];
            newItem[index].width = width;
            rearrangeColumns(newItem);
        }
        setResizable(false);
    };

    const visibleItems = items.filter(i => i.show);
    const lastVisibleId = visibleItems[visibleItems.length - 1]?.id;
    const columnWidth =
        item.id === lastVisibleId
            ? 'auto'
            : `${95 / (visibleItems.length + 1)}%`;

    return (
        <TableCell
            key={id}
            align="left"
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            style={{
                textTransform: 'capitalize',
                width: columnWidth,
                ...style,
            }}
            sx={{
                ...(isSortableOverlay && {
                    '&.MuiTableCell-root': {
                        width: '0px !important',
                        minWidth: '0px',
                        padding: '0px !important',
                    },
                }),
            }}
            padding="normal"
            id={`header${index}`}
            sortDirection={
                Object.values(item).includes(orderBy)
                    ? order
                    : false
            }
        >
            <Resizable
                defaultSize={{ width: item.width ? item.width : 'auto', height: 'auto' }}
                enable={{
                    top: false,
                    right: resizableHeader,
                    bottom: false,
                    left: false,
                    topRight: false,
                    bottomRight: false,
                    bottomLeft: false,
                    topLeft: false,
                }}
                style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                }}
                className={
                    resizableHeader && (!(activeObject && (activeObject.id === item.id))) ? customStyles['resizable-column'] : ''
                }
                onResizeStart={(e) => {
                    setResizable(true);
                    e.stopPropagation();
                    e.preventDefault();
                    document.body.style.setProperty('cursor', 'col-resize', 'important');
                }}
                onResizeStop={(e, dir, ref) => {
                    resizeEnd(ref.style.width);
                    document.body.style.removeProperty('cursor');
                    e.stopPropagation();
                    e.preventDefault();
                }}
            >
                {(index === 0 && isGroup) && getIcon('all', isExpanded)}
                {onRequestSort && !item.disableSort ?
                    <TableSortLabel
                        active={(id === orderBy)}
                        direction={order}
                        onClick={
                            createSortHandler(id)
                        }
                        classes={{
                            root: classes.root,
                        }}
                    >
                        {item[id]}
                        {item.displayUnit && <span style={{ textTransform: 'none' }}>&nbsp;({item.displayUnit})</span>}
                    </TableSortLabel>
                    :
                    <span>
                        {item[id]}
                        {item.displayUnit && <span style={{ textTransform: 'none' }}>&nbsp;({item.displayUnit})</span>}
                    </span>
                }
            </Resizable>
        </TableCell>
    );
}

SortableItem.propTypes = {
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    rearrangeColumns: PropTypes.func,
    onRequestSort: PropTypes.func,
    createSortHandler: PropTypes.func.isRequired,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
    ).isRequired,
    item: PropTypes.shape({
        id: PropTypes.string,
        disableSort: PropTypes.bool,
        displayUnit: PropTypes.string,
        width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
    index: PropTypes.number.isRequired,
    enableColumnRearrange: PropTypes.bool,
    resizableHeader: PropTypes.bool,
    orderBy: PropTypes.string,
    order: PropTypes.oneOf(['asc', 'desc']),
    isGroup: PropTypes.bool,
    isExpanded: PropTypes.bool,
    getIcon: PropTypes.func,
    classes: PropTypes.shape({
        root: PropTypes.string,
    }),
    setActiveObject: PropTypes.func,
    activeObject: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    setResizable: PropTypes.func.isRequired,
    isResizing: PropTypes.bool,
    isSortableOverlay: PropTypes.bool,
};
